import { DOCUMENT } from '@angular/common'
import { inject, Injectable, InjectionToken } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { filter } from 'rxjs/operators'
import { environment } from '../../environments/environment'
import { buildCanonicalHref, ensureHttps, resolveCanonicalBaseFromPublicHost } from "../helpers";

export const PUBLIC_HOST = new InjectionToken<string>('PUBLIC_HOST')

@Injectable({ providedIn: 'root' })
export class CanonicalService {
  private readonly document = inject(DOCUMENT)
  private readonly router = inject(Router)
  private readonly publicHost = inject(PUBLIC_HOST, { optional: true }) as string | null

  init(): void {
    this.updateCanonical(this.router.url)
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => this.updateCanonical(event.urlAfterRedirects))
  }

  /**
   * Setzt den Canonical-Tag explizit auf den übergebenen Pfad. Wird z. B. von
   * der CategoryComponent genutzt, um bei Kategorien ohne Beschreibung die
   * Hauptkategorie als Canonical-URL zu verwenden.
   */
  setCanonicalUrl(path: string): void {
    this.updateCanonical(path)
  }

  private updateCanonical(routerUrl: string): void {
    const canonicalHref = buildCanonicalHref(this.resolveBaseUrl(), routerUrl)

    const head = this.document.head
    if (!head) {
      return
    }

    let link = head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!link) {
      link = this.document.createElement('link')
      link.setAttribute('rel', 'canonical')
      head.appendChild(link)
    }
    link.setAttribute('href', canonicalHref)
  }

  private resolveBaseUrl(): string {
    const fromPublicHost = resolveCanonicalBaseFromPublicHost(this.publicHost)
    if (fromPublicHost) {
      return ensureHttps(fromPublicHost)
    }
    const loc = (this.document as Document).defaultView?.location
    if (loc && loc.origin) {
      return ensureHttps(loc.origin.replace(/\/+$/, ''))
    }
    return ensureHttps((environment.cmsBaseUrl || '').replace(/\/+$/, ''))
  }
}
